import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Plus, Pencil, Trash2, X, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emptyDraft = () => ({ id: null, quote: "", name: "", business: "", rating: 5 });

export default function TestimonialsManager({ secret }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const auth = { headers: { "X-Admin-Secret": secret } };

  const load = async () => {
    setLoading(true);
    try { setItems((await axios.get(`${API}/testimonials`)).data || []); }
    catch { setError("Could not load testimonials."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    if (draft.quote.trim().length < 3 || !draft.name.trim()) { setError("Quote and name are required."); return; }
    setSaving(true); setError("");
    const payload = { quote: draft.quote.trim(), name: draft.name.trim(), business: draft.business.trim(), rating: Number(draft.rating) };
    try {
      if (draft.id) await axios.put(`${API}/testimonials/${draft.id}`, payload, auth);
      else await axios.post(`${API}/testimonials`, payload, auth);
      setDraft(null); await load();
    } catch { setError("Save failed. Check the admin secret and try again."); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try { await axios.delete(`${API}/testimonials/${id}`, auth); await load(); }
    catch { setError("Delete failed."); }
  };

  const field = "mt-2 w-full bg-transparent border-b border-[var(--hairline)] py-2 text-base focus:outline-none focus:border-[var(--ink)]";
  const label = "block mt-5 text-xs uppercase tracking-[0.2em] text-[var(--muted-ink)] font-semibold";

  return (
    <div data-testid="testimonials-manager">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold tracking-tight text-3xl sm:text-4xl">Client testimonials</h1>
          <p className="mt-2 text-[var(--muted-ink)]" data-testid="testimonials-count">
            {loading ? "Loading…" : `${items.length} published`} · shown above the lead form once at least one is published
          </p>
        </div>
        {!draft && (
          <button onClick={() => { setDraft(emptyDraft()); setError(""); }} data-testid="add-testimonial-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[3px] bg-[var(--ink)] text-[var(--page)] text-xs font-semibold uppercase tracking-wide hover:opacity-90">
            <Plus className="h-4 w-4" /> Add testimonial
          </button>
        )}
      </div>

      {error && <p className="mt-4 text-sm font-medium" data-testid="testimonials-error">{error}</p>}

      {draft && (
        <div className="mt-8 border border-[var(--hairline)] rounded-[4px] p-6 sm:p-8" data-testid="testimonial-editor">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl">{draft.id ? "Edit testimonial" : "New testimonial"}</h2>
            <button onClick={() => setDraft(null)} data-testid="cancel-testimonial-btn" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--muted-ink)] hover:text-[var(--ink)]">
              <X className="h-4 w-4" /> Cancel
            </button>
          </div>
          <label className={label}>Quote *</label>
          <textarea data-testid="testimonial-quote-input" rows={3} value={draft.quote} onChange={(e) => setDraft((d) => ({ ...d, quote: e.target.value }))} placeholder="What the client said…" className={field} />
          <div className="grid sm:grid-cols-2 gap-x-8">
            <div>
              <label className={label}>Client name / initials *</label>
              <input data-testid="testimonial-name-input" value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder="e.g. R. Sharma" className={field} />
            </div>
            <div>
              <label className={label}>Business / role</label>
              <input data-testid="testimonial-business-input" value={draft.business} onChange={(e) => setDraft((d) => ({ ...d, business: e.target.value }))} placeholder="e.g. Resort owner, Denarau" className={field} />
            </div>
          </div>
          <label className={label}>Rating</label>
          <div className="mt-2 flex gap-1" data-testid="testimonial-rating-input">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" data-testid={`rating-star-${n}`} onClick={() => setDraft((d) => ({ ...d, rating: n }))} aria-label={`${n} stars`}>
                <Star className={`h-6 w-6 ${n <= draft.rating ? "fill-current" : ""}`} strokeWidth={1.6} />
              </button>
            ))}
          </div>
          <button onClick={save} disabled={saving} data-testid="save-testimonial-btn" className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-[3px] bg-[var(--ink)] text-[var(--page)] text-xs font-semibold uppercase tracking-wide hover:opacity-90 disabled:opacity-60">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : draft.id ? "Save changes" : "Publish"}
          </button>
        </div>
      )}

      {loading ? (
        <div className="mt-10 flex items-center gap-2 text-[var(--muted-ink)]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <ul className="mt-10 divide-y divide-[var(--hairline)]" data-testid="testimonials-list">
          {items.length === 0 && <li className="py-8 text-[var(--muted-ink)]" data-testid="testimonials-empty">No testimonials yet — the strip is hidden on the site until you publish one.</li>}
          {items.map((it) => (
            <li key={it.id} className="py-5 flex items-start justify-between gap-6" data-testid={`testimonial-row-${it.id}`}>
              <div className="min-w-0">
                <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-3.5 w-3.5 ${n <= it.rating ? "fill-current" : ""}`} strokeWidth={1.6} />)}</div>
                <p className="mt-2 text-base leading-relaxed">“{it.quote}”</p>
                <p className="mt-1 text-sm text-[var(--muted-ink)]"><span className="font-semibold text-[var(--ink)]">{it.name}</span>{it.business ? ` · ${it.business}` : ""}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => { setDraft({ ...emptyDraft(), ...it }); setError(""); }} data-testid={`edit-testimonial-${it.id}`} className="h-9 w-9 inline-flex items-center justify-center rounded-[3px] border border-[var(--hairline)] hover:bg-[var(--ink)]/5"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(it.id)} data-testid={`delete-testimonial-${it.id}`} className="h-9 w-9 inline-flex items-center justify-center rounded-[3px] border border-[var(--hairline)] hover:bg-[var(--ink)]/5"><Trash2 className="h-4 w-4" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
